import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

import client from '@/lib/http/client';
import {
	IGetCategoriesCreatorsResponse,
	IGetCreatorResponse,
	IOptions,
	IUseGetCategoriesProps,
	IUseGetCreatorsProps,
} from '@/ts';

export function prepareParams(options: any) {
	const { search, filters, ...rest } = options;

	if (filters && filters?.length > 0) {
		return {
			...rest,
			platforms: filters,
			search,
		};
	}

	return {
		...rest,
		search,
	};
}

function getAllCreators(options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(`/creators`, {
		params,
	} as AxiosRequestConfig);
}

function getCreatorsByIABCategories(
	iabCategoryNames: string[],
	options: IOptions,
) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(
		`/creators/byIABCategoryNames/${iabCategoryNames.join(',')}`,
		{
			params,
		} as AxiosRequestConfig,
	);
}

function getCreatorsByCategoryId(id: string, options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(
		`/creators/byCategoryId/${id}`,
		{
			params,
		} as AxiosRequestConfig,
	);
}

function getCreatorContent(id: string, options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(`/content/creator/${id}`, {
		params,
	} as AxiosRequestConfig);
}

function getCreatorsByCategoryName(name: string, options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(
		`/creators/byCategoryName/${name}`,
		{
			params,
		} as AxiosRequestConfig,
	);
}

function getCreatorsUrlsByCategoryId(id: string) {
	return client.get<IGetCategoriesCreatorsResponse>(
		`/creators/downloadUrls/${id}`,
		{
			responseType: 'blob', // important
		},
	);
}

function getCreatorById(id: string) {
	return client.get<IGetCreatorResponse>(`/creators/${id}`);
}

function groupUngroupCreators({
	ids,
	ungroup = false,
}: {
	ids: string[];
	ungroup?: boolean;
}) {
	return client.put<IGetCreatorResponse>(
		`/creators/${ungroup ? 'ungroup' : 'group'}`,
		ids,
	);
}

export function useGetAllCreators({
	pagination,
	sortBy,
	sortOrder,
	search,
	filters,
}: IUseGetCreatorsProps) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['creators', page, limit, sortBy, sortOrder, filters, search],
		() =>
			getAllCreators({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
				filters,
			} as IOptions),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
		},
	);
}

export function useGetCreatorsByIABCategories(
	iabCategoryNames: string[],
	{ pagination, sortBy, sortOrder, search }: IUseGetCategoriesProps,
) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['creators/category', page, limit, sortBy, sortOrder, search],
		() =>
			getCreatorsByIABCategories(iabCategoryNames, {
				page,
				limit,
				sortBy,
				sortOrder,
				search,
			} as IOptions),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
		},
	);
}

export function useGetCreatorsByCategoryId(
	id: string,
	{ pagination, sortBy, sortOrder, search, filters }: IUseGetCategoriesProps,
	enabled = false,
) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		[`creators/category${id}`, page, limit, sortBy, filters, sortOrder, search],
		() =>
			getCreatorsByCategoryId(id, {
				page,
				limit,
				sortBy,
				sortOrder,
				search,
				filters,
			} as IOptions),
		{
			keepPreviousData: true,
			staleTime: 1000 * 60 * 5,
			refetchOnWindowFocus: false,
			retry: 3,
			refetchInterval: enabled ? 10 * 1000 : false,
		},
	);
}

export function useGetCreatorContent(
	id: string,
	{ pagination, sortBy, sortOrder, search, filters }: IUseGetCategoriesProps,
	enabled = false,
) {
	const { page = 1, limit = 10 } = pagination;

	return useQuery(
		[`creators/content`, id, page, limit, sortBy, filters, sortOrder, search],
		() =>
			getCreatorContent(id, {
				page,
				limit,
				sortBy,
				sortOrder,
				search,
				filters,
			} as IOptions),
		{
			keepPreviousData: true,
			staleTime: 1000 * 60 * 5,
			refetchOnWindowFocus: false,
			retry: 3,
			refetchInterval: enabled ? 10 * 1000 : false,
		},
	);
}
// export function useGetCreatorsByCategoryName(
//   name: string,
//   { pagination, sortBy, sortOrder, search, filters }: IUseGetCategoriesProps,
//   enabled: boolean
// ) {
//   const { page = 1, limit = 20 } = pagination;

//   return useQuery(
//     [
//       `creators/category${name}`,
//       page,
//       limit,
//       sortBy,
//       filters,
//       sortOrder,
//       search,
//     ],
//     () =>
//       getCreatorsByCategoryName(name, {
//         page,
//         limit,
//         sortBy,
//         sortOrder,
//         search,
//         filters,
//       } as IOptions),
//     {
//       keepPreviousData: true,
//       staleTime: 1000 * 60 * 5,
//       refetchOnWindowFocus: false,
//       // enabled,
//       retry: 3,
//       refetchInterval: enabled ? 10 * 1000 : false,
//     }
//   );
// }

export function useGetCreatorsUrlByCategoryId(id: string) {
	return useQuery(
		[`creators/category_file_${id}`],
		() => getCreatorsUrlsByCategoryId(id),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			enabled: false,
			retry: 3,
		},
	);
}

export function useGetCreatorById(id: string) {
	return useQuery([`creator/${id}`], () => getCreatorById(id), {
		keepPreviousData: true,
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
		retry: 3,
	});
}

export function useGroupUngroupCreators() {
	const queryClient = useQueryClient();

	return useMutation(groupUngroupCreators, {
		onSuccess: () => {
			queryClient.invalidateQueries(['creators']);
		},
	});
}

function deleteCreatorById(id: string) {
	return client.delete(`/creators/${id}`);
}

export function useDeleteCreator() {
	const queryClient = useQueryClient();

	return useMutation(deleteCreatorById, {
		onSuccess: () => {
			queryClient.invalidateQueries(['creators']);
		},
	});
}
